import Link from 'next/link';
import styled from 'styled-components';

import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CONTAINER_BREAKPOINTS, CONTAINER_MAX } from '@/constants/breakpoints';
import { ParticipantEnum } from '@/constants/common';
import {
  containerPaddingInline,
  flexCenterAligned,
  flexColumn
} from '@/styles/common';

const withBannerPadding = (
  paddingWithoutBanner: number,
  paddingWithBanner: number
) => {
  const reduction = Math.max(paddingWithoutBanner - paddingWithBanner, 0);
  return `calc(${paddingWithoutBanner}px - clamp(0px, var(--banner-header-offset, 0px), ${reduction}px))`;
};

export const Wrapper = styled.div`
  ${flexColumn};
`;

export const Section = styled.div<{ $isLast: boolean }>`
  position: relative;
  transition: background-color 0.3s linear;
  margin-bottom: ${({ $isLast }) => ($isLast ? '70px' : '50px')};
  ${containerPaddingInline};

  ${flexColumn};

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    margin-bottom: 30px;
  }
`;

export const MainContentWrapper = styled.div<{
  $isResumes?: boolean;
}>`
  /* The page content is capped at CONTENT_MAX_WIDTH by PageWrapper's <main>,
     but this hero background (color + DashboardBackgroundIcon) must stay
     full-bleed. Break out of the capped parent to the full viewport width;
     body has overflow-x: hidden, so this never adds horizontal scroll. */
  width: 100vw;
  margin-inline: calc(50% - 50vw);
  background-color: ${({ $isResumes }) =>
    $isResumes ? 'var(--brand-main)' : 'var(--bg-card)'};
  padding-top: ${withBannerPadding(100, 120)};
  padding-bottom: 100px;
  ${containerPaddingInline};
  overflow: hidden;
  position: relative;

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    padding-top: ${withBannerPadding(40, 120)};
    padding-bottom: 40px;
  }
`;

export const SectionHeader = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
`;

export const SectionContent = styled.div`
  position: relative;
  margin: auto;
  ${flexColumn};
  align-items: center;
  gap: 40px;
  z-index: 1;
`;

export const SearchBlockTitle = styled(Typography).attrs({ variant: 'h0' })<{
  $isResumes?: boolean;
}>`
  font-size: 36px;
  max-width: 594px;
  width: 100%;
  text-align: center;
  margin-bottom: 20px;
  color: ${({ $isResumes }) =>
    $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    text-align: left;
    margin-bottom: 0;
    font-size: 20px;
  }
`;

export const SearchBlockWrapper = styled.div`
  ${flexCenterAligned};
  flex-direction: column;
  row-gap: 30px;
  font-size: 36px;
  text-align: center;
  width: 100%;

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    align-items: baseline;
  }

  @media (min-width: ${CONTAINER_BREAKPOINTS.desktop}) {
    max-width: 800px;
  }
`;

export const BackgroundIconWrapper = styled.div<{
  $participant: ParticipantEnum;
}>`
  overflow: hidden;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  & svg {
    transition: color 0.3s linear;
    margin-right: -5%;
    margin-top: -24px;
    color: ${({ $participant }) =>
      `var(--dashboard-${$participant.toLowerCase()}-icon)`};
  }

  @media (max-width: ${CONTAINER_MAX.mobileL}) {
    bottom: -100px;
    left: 120px;
    right: -60px;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--textAndLabels-main);

  & > svg * {
    fill: var(--textAndLabels-main);
  }
`;

export const ContentWrapper = styled.div`
  background: var(--bg-main);

  z-index: 3;
  padding-top: 30px;

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    padding-top: 20px;
  }
`;

export const StatisticLink = styled(Link)<{
  $isDark?: boolean;
  $isResumes?: boolean;
}>`
  ${flexCenterAligned};
  gap: 3px;
  color: ${({ $isResumes }) =>
    $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background-color: ${({ $isResumes }) =>
      $isResumes ? 'var(--textAndLabels-dark)' : 'var(--textAndLabels-main)'};
    position: absolute;
    bottom: 0;
    left: 0;
  }
`;

export const StatisticTitle = styled(Typography).attrs({ variant: 'body1' })`
  ${flexCenterAligned};

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    justify-content: flex-start;
  }
`;

export const HightlightText = styled.span<{
  $isResumes?: boolean;
  $isDark?: boolean;
}>`
  color: ${({ $isResumes }) => {
    return $isResumes ? 'var(--textAndLabels-dark)' : 'var(--system-yellow)';
  }};
`;

export const TypographyCenter = styled(Typography)`
  text-align: center;
`;

export const DesktopSubFooter = styled.div`
  @media (max-width: ${CONTAINER_MAX.tablet}) {
    display: none;
  }
`;

export const MobileSubFooter = styled.div`
  border-top: 1px solid var(--border-main);
  padding-block: 30px 10px;
  border-bottom: 1px solid var(--bg-main);
  margin-bottom: -1px;
  position: relative;

  @media (min-width: ${CONTAINER_BREAKPOINTS.tablet}) {
    display: none;
  }
`;

export const NoDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding-block: 100px;
  ${containerPaddingInline};
`;
